import {
  ToolbarChildItem,
  ToolbarItem,
  ToolbarRootItem
} from "../../model/menu";
import { urlForDocument } from "../../urls";

const stripLanguageIfPresent = (path: string, language: string | undefined) =>
  language
    ? path.replace(new RegExp(`^/${language.toLowerCase()}/`), "/")
    : path;

const getURL = (
  item: ToolbarRootItem | ToolbarChildItem
): string | undefined => {
  if (item.internalLink) {
    return urlForDocument(item.internalLink);
  }
};

const enum MatchType {
  None,
  Exact,
  Loose
}

/**
 * Strip the last path component from the URL.
 * If there is only one path component then return undefined.
 * Maintains (but does not check) the invariant that our pathnames start and end with a slash.
 */
export const stripLastPathComponent = (pathname: string) => {
  // Note this has leading and trailing empty strings
  const parts = pathname.split("/");
  parts.pop();
  parts.pop();
  if (parts.length === 1) {
    return undefined;
  }
  parts.push("");
  return parts.join("/");
};

export const findNavMatches = (
  pathname: string,
  toolbar: ToolbarItem[],
  language: string | undefined
): ToolbarItem[] => {
  const findPathToLeafMatchingPathname = (
    items: ToolbarItem[],
    pathname: string,
    parents: ToolbarItem[] = []
  ): ToolbarItem[] | undefined => {
    for (const item of items) {
      if (item.childLinks && item.childLinks.length > 0) {
        const result = findPathToLeafMatchingPathname(
          item.childLinks,
          pathname,
          [...parents, item]
        );
        if (result) {
          return result;
        }
      }
      const url = getURL(item);
      if (url) {
        const matchType = matchURL(url, pathname, language, !parents.length);
        switch (matchType) {
          case MatchType.Exact: {
            return [...parents, item];
          }
          case MatchType.Loose: {
            return [item];
          }
          case MatchType.None:
            break;
        }
      }
    }
    return undefined;
  };

  for (
    let remainder: string | undefined = pathname;
    remainder;
    remainder = stripLastPathComponent(remainder)
  ) {
    const result = findPathToLeafMatchingPathname(toolbar, remainder);
    if (result) {
      return result;
    }
  }
  return [];
};

const matchURL = (
  url: string,
  pathname: string,
  language: string | undefined,
  isRoot: boolean
): MatchType => {
  url = stripLanguageIfPresent(url, language);
  pathname = stripLanguageIfPresent(pathname, language);
  if (
    url === pathname ||
    (!isRoot &&
      stripLastPathComponent(url) === pathname &&
      pathname !== "/teach/")
  ) {
    return MatchType.Exact;
  }
  // This case enables something dubious: The highlighting of a section like
  // "Teach" which links to /teach/featured in the scenario where some random page
  // has been shoved into "Teach" that doesn't match any of the second level nav.
  if (isRoot && stripLastPathComponent(url) === pathname) {
    return MatchType.Loose;
  }
  return MatchType.None;
};
